import { useState } from "react";
import { SpeakerHigh } from "@phosphor-icons/react";

/**
 * PronounceButton — VAGUE 29 (2026-02-23).
 *
 * Prononce le nom du prospect via l'API SpeechSynthesis du navigateur
 * (100% gratuit, hors ligne, aucun appel externe). Aide les télépros
 * face aux noms d'origines variées (turcs, arabes, slaves, africains…)
 * — l'Alsace compte 157 nationalités et autant de patronymes.
 *
 * Détecte automatiquement l'accent probable via des patterns simples
 * (ex : "-oğlu" → tr, "al-" → ar) et choisit la voix la plus adaptée
 * parmi celles disponibles dans le navigateur, avec fallback fr-FR.
 */
function guessLangHint(name) {
  const n = (name || "").toUpperCase();
  // Arabic / Maghreb patterns
  if (/\b(EL|AL|BEN|IBN|OULD|ABD|BOU|SIDI)\b/.test(n) || /(OU[SR]|HOU|GHA|KHA|CHA[FT])/.test(n)) return "ar";
  // Turkish
  if (/(OĞLU|OGLU|ÖZ|GÜL|GUR|SEN|SAN|BEY|PASA|EFE)/.test(n)) return "tr";
  // Slavic / Russian / Polish / Serbo-Croatian
  if (/(SKI|SKY|OWICZ|CZAK|KOWSKI|OVIC|OVA|EVA|ENKO|USHKA|AKOV)/.test(n)) return "pl";
  // Portuguese / Brazilian
  if (/(OES|EIRO|EIRA|INHO|ANHA|ÇÃO)/.test(n)) return "pt";
  // Spanish
  if (/(EZ|IEZ|ANDEZ|RIGUEZ|ARCIA|ARTIN)\b/.test(n)) return "es";
  // Italian
  if (/(INI|ELLI|ETTI|OTTO|ANO|ARO|UCCI)\b/.test(n)) return "it";
  // Asian (Vietnamese common)
  if (/\b(NGUYEN|TRAN|PHAM|HUYNH|LE|VU|VO|DANG|PHAN|BUI)\b/.test(n)) return "vi";
  // German (Alsace context)
  if (/(STEIN|BAUER|SCHM|FISCH|WEIN|MULL|WALD|BERG|HOFF|MEISTER)/.test(n)) return "de";
  return "fr";
}


// Cache the voices list once — SpeechSynthesis.getVoices() is idempotent
let _voiceCache = null;
function pickVoice(langHint) {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  if (!_voiceCache) _voiceCache = window.speechSynthesis.getVoices() || [];
  const voices = _voiceCache;
  if (!voices.length) return null;
  // exact language match first
  let v = voices.find((x) => (x.lang || "").toLowerCase().startsWith(langHint));
  if (v) return v;
  // French fallback
  v = voices.find((x) => (x.lang || "").toLowerCase().startsWith("fr"));
  return v || voices[0];
}


export default function PronounceButton({ text, testid }) {
  const [speaking, setSpeaking] = useState(false);
  if (!text || typeof window === "undefined" || !window.speechSynthesis) {
    return null;
  }
  const speak = () => {
    try {
      const ss = window.speechSynthesis;
      ss.cancel();
      // Refresh cache once — voices load asynchronously on some browsers
      _voiceCache = ss.getVoices() || [];
      const lang = guessLangHint(text);
      const utter = new SpeechSynthesisUtterance(text);
      const voice = pickVoice(lang);
      if (voice) utter.voice = voice;
      utter.lang = voice?.lang || (lang === "fr" ? "fr-FR" : `${lang}-${lang.toUpperCase()}`);
      utter.rate = 0.9;
      utter.pitch = 1;
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      setSpeaking(true);
      ss.speak(utter);
    } catch {
      setSpeaking(false);
    }
  };
  return (
    <button
      type="button"
      onClick={speak}
      title="Prononcer le nom (aide face aux origines variées)"
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm border transition-colors ${
        speaking
          ? "bg-primary text-primary-foreground border-primary"
          : "border-border text-muted-foreground hover:text-primary hover:border-primary/60 hover:bg-primary/5"
      }`}
      data-testid={testid || "pronounce-btn"}
      aria-label={`Prononcer ${text}`}
    >
      <SpeakerHigh size={11} weight={speaking ? "fill" : "bold"} />
      {speaking ? "…" : "Prononcer"}
    </button>
  );
}
